import { ContractPopover } from '@/entities/contract'
import { formatCurrency } from '@/shared/lib'
import { Icon, Separator, ContentBox, Description } from '@/shared/ui'

import { UpdateDepositReturnAccountButton } from '../deposit-return-account-popup/show-dialog-button'
import { UpdateDepositReturnCardButton } from '../deposit-return-card-popup/show-dialog-button'

export type Props = {
  usingFee: number
  longTermDiscount: number
  earlyDiscount: number
  mgmtFee: number
  cleanFee: number
  contractFee: number
  realUsingFee: number
  deposit: number
  finalPayFee: number
  isCancelled: boolean
  isExtend: boolean
  hasDepositReturnAccountInfo: boolean
  isDepositReturnAccountButtonVisible: boolean
  isDepositReturnCardButtonVisible: boolean
}

export const GuestPayInformationDetail = ({
  usingFee,
  longTermDiscount,
  earlyDiscount,
  mgmtFee,
  cleanFee,
  contractFee,
  realUsingFee,
  deposit,
  finalPayFee,
  isCancelled,
  isExtend,
  hasDepositReturnAccountInfo,
  isDepositReturnAccountButtonVisible,
  isDepositReturnCardButtonVisible,
}: Props) => {
  const showDepositReturnAccountTooltip = !hasDepositReturnAccountInfo

  const showLongTermDiscount = longTermDiscount > 0
  const showEarlyDiscount = earlyDiscount > 0

  return (
    <div className="space-y-3">
      <Description.List>
        <Description.Item
          className="justify-between"
          label="임대료"
          value={`${formatCurrency(usingFee)}원`}
          lineThrough={isCancelled}
        />

        {showLongTermDiscount && (
          <Description.Item className="justify-between">
            <Description.ItemLabel highlight>
              장기계약 할인
            </Description.ItemLabel>
            <Description.ItemValue highlight lineThrough={isCancelled}>
              {formatCurrency(longTermDiscount, { negative: true })}원
            </Description.ItemValue>
          </Description.Item>
        )}

        {showEarlyDiscount && (
          <Description.Item className="justify-between">
            <Description.ItemLabel highlight>
              즉시입주 할인
            </Description.ItemLabel>
            <Description.ItemValue highlight lineThrough={isCancelled}>
              {formatCurrency(earlyDiscount, { negative: true })}원
            </Description.ItemValue>
          </Description.Item>
        )}

        <Description.Item
          className="justify-between"
          label="관리비"
          value={`${formatCurrency(mgmtFee)}원`}
          lineThrough={isCancelled}
        />

        <Description.Item className="items-start justify-between">
          <Description.ItemLabel>
            <span>청소비</span>
            {isExtend && (
              <span className="typo-label2 block font-normal">
                * 연장계약에서는 청소비가 청구되지 않습니다.
              </span>
            )}
          </Description.ItemLabel>
          <Description.ItemValue lineThrough={isCancelled}>
            {formatCurrency(cleanFee)}원
          </Description.ItemValue>
        </Description.Item>

        <Description.Item
          className="justify-between"
          label="계약 수수료"
          value={`${formatCurrency(contractFee)}원`}
          lineThrough={isCancelled}
        />
      </Description.List>

      <Separator />

      <Description.List>
        <Description.Item className="justify-between">
          <Description.ItemLabel semiBold className="text-black">
            실 이용 금액
          </Description.ItemLabel>
          <Description.ItemValue
            bold
            highlight
            className="typo-headline3"
            lineThrough={isCancelled}
          >
            {formatCurrency(realUsingFee)}원
          </Description.ItemValue>
        </Description.Item>

        <Description.Item
          className="justify-between"
          label="보증금"
          value={`${formatCurrency(deposit)}원`}
          lineThrough={isCancelled}
        />

        <ContentBox
          color="purple"
          className="px-3"
          align={
            isDepositReturnAccountButtonVisible ||
            isDepositReturnCardButtonVisible
              ? 'between'
              : 'center'
          }
        >
          {(() => {
            if (isExtend) {
              return '* 연장 계약시 보증금은 추가 결제되지 않습니다.'
            }

            return '* 보증금은 삼삼엠투에서 보관하며, 퇴실 후 반환됩니다.'
          })()}

          {isDepositReturnAccountButtonVisible && (
            <ContractPopover
              defaultOpen={showDepositReturnAccountTooltip}
              message={`보증금 반환 계좌\n정보를 입력해 주세요`}
            >
              <UpdateDepositReturnAccountButton color="purple">
                {(() => {
                  if (hasDepositReturnAccountInfo) {
                    return '보증금 반환 정보 확인하기'
                  }

                  return '보증금 반환 정보 입력하기'
                })()}
                <Icon name="arrow-right" />
              </UpdateDepositReturnAccountButton>
            </ContractPopover>
          )}

          {isDepositReturnCardButtonVisible && (
            <UpdateDepositReturnCardButton color="purple">
              보증금 반환 정보 확인하기
              <Icon name="arrow-right" />
            </UpdateDepositReturnCardButton>
          )}
        </ContentBox>
      </Description.List>

      <Separator color="black" />

      <Description.List>
        <Description.Item className="justify-between">
          <Description.ItemLabel bold className="text-black">
            최종 결제 금액
          </Description.ItemLabel>
          <Description.ItemValue bold className="typo-headline3">
            {formatCurrency(finalPayFee)}원
          </Description.ItemValue>
        </Description.Item>
      </Description.List>
    </div>
  )
}
